import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, XCircle, Loader2, Phone, Home } from "lucide-react";

import { API } from "@/lib/api";
import InstallBanner from "@/components/InstallBanner";
import { navigateAfterFirstBooking } from "@/lib/install";

const SUPPORT_PHONE = "1 (484) 577-3677";

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading");
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const sessionId = searchParams.get("session_id");
  const bookingId = searchParams.get("booking_id");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (pollCount >= 10) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      setPaymentDetails(response.data);

      if (response.data.payment_status === "paid") {
        setStatus("success");
      } else if (response.data.status === "expired") {
        setStatus("expired");
      } else {
        // Continue polling
        setPollCount(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (err) {
      console.error("Payment status error:", err);
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-5">
      <Card className="bg-[#121212] border-white/5 max-w-md w-full" data-testid="payment-status-card">
        <CardContent className="p-8 text-center">
          {status === "loading" && (
            <>
              <Loader2 className="w-16 h-16 text-[#4D88FF] mx-auto mb-6 animate-spin" />
              <h2 className="text-2xl font-bold text-white mb-2">Processing Payment</h2>
              <p className="text-zinc-400">Please wait while we confirm your payment...</p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-emerald-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2" data-testid="success-title">
                Payment Successful!
              </h2>
              <p className="text-zinc-400 mb-4">Your booking has been confirmed.</p>
              
              {paymentDetails && (
                <div className="bg-zinc-800 rounded-lg p-4 mb-6 text-left">
                  <div className="flex justify-between mb-2">
                    <span className="text-zinc-400">Amount Paid</span>
                    <span className="text-emerald-400 font-bold">
                      ${paymentDetails.amount?.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Status</span>
                    <span className="text-emerald-400">Confirmed</span>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <Button
                  onClick={() => {
                    const bid = bookingId || sessionStorage.getItem('link_pending_booking');
                    sessionStorage.removeItem('link_pending_booking');
                    if (bid) {
                      navigateAfterFirstBooking(navigate, bid);
                    } else {
                      navigate('/', { replace: true });
                    }
                  }}
                  className="w-full bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold rounded-xl shadow-[0_4px_24px_-8px_rgba(0,71,255,0.6)] border-0"
                  data-testid="track-order-btn"
                >
                  Track My Order
                </Button>

                {/* Highest-intent moment: customer JUST paid — perfect time to nudge install */}
                <InstallBanner testIdPrefix="success-install-banner" />
                <Button
                  onClick={() => navigate("/")}
                  variant="outline"
                  className="w-full border-white/10 text-zinc-300"
                  data-testid="go-home-btn"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
                <Button
                  onClick={() => window.open(`tel:${SUPPORT_PHONE}`, '_self')}
                  variant="ghost"
                  className="w-full text-zinc-400"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Questions? Call Us
                </Button>
              </div>
            </>
          )}

          {(status === "error" || status === "expired" || status === "timeout") && (
            <>
              <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2" data-testid="error-title">
                {status === "expired" ? "Payment Expired" : "Payment Issue"}
              </h2>
              <p className="text-zinc-400 mb-6">
                {status === "expired" 
                  ? "Your payment session expired. Please try again."
                  : status === "timeout"
                  ? "We couldn't confirm your payment. Please contact support."
                  : "Something went wrong with your payment."}
              </p>

              <div className="space-y-3">
                <Button
                  onClick={() => navigate("/")}
                  className="w-full bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold rounded-xl shadow-[0_4px_24px_-8px_rgba(0,71,255,0.6)] border-0"
                >
                  Try Again
                </Button>
                <Button
                  onClick={() => window.open(`tel:${SUPPORT_PHONE}`, '_self')}
                  variant="outline"
                  className="w-full border-white/10 text-zinc-300"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Contact Support
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
